// PHSIQ wishlist drawer: lists saved products (from assets/wishlist.js) with
// variant picker, quick add-to-bag and remove. Stays in sync via wishlist:* events.
(() => {
  if (customElements.get('phsiq-wishlist-drawer')) return

  const STORAGE_KEY = 'm-wishlist-products'
  const TRIGGERS = '.m-header__wishlist, .m-mobile-sticky-bar--wishlist, [data-pwd-open]'
  const productCache = new Map()

  const esc = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

  const money = (cents) => {
    const format = window.MinimogSettings?.money_format
    return typeof window.formatMoney === 'function' && format ? window.formatMoney(cents, format) : (cents / 100).toFixed(2)
  }

  const sizedImage = (src, width = 240) => {
    if (!src) return ''
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin)
      url.searchParams.set('width', width)
      return url.toString()
    } catch (e) {
      return src
    }
  }

  const readHandles = () => {
    if (window.MinimogTheme?.Wishlist) return [...window.MinimogTheme.Wishlist.products]
    try {
      return Array.from(new Set(JSON.parse(localStorage.getItem(STORAGE_KEY)) || []))
    } catch (e) {
      return []
    }
  }

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/'
      productCache.set(
        handle,
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      )
    }
    return productCache.get(handle)
  }

  // Shared add-to-bag used by the drawer and the wishlist page. Renders into the
  // theme cart drawer when it's enabled, otherwise goes to /cart.
  window.PhsiqCart = window.PhsiqCart || {
    async add(variantId, { beforeOpen } = {}) {
      const root = window.Shopify?.routes?.root || '/'
      const cartDrawer = document.querySelector('m-cart-drawer')
      const useDrawer = !!(cartDrawer?.getSectionsToRender && window.MinimogSettings?.enable_cart_drawer)
      const formData = new FormData()
      formData.append('id', variantId)
      formData.append('quantity', 1)
      if (useDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map((s) => s.id))
        formData.append('sections_url', window.location.pathname)
      }
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
      const data = await res.json()
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag')

      if (!useDrawer || !data.sections) {
        window.location.href = `${root}cart`
        return data
      }
      beforeOpen?.()
      cartDrawer.renderContents(data)
      fetch(`${root}cart.js`)
        .then((r) => r.json())
        .then((cart) => cartDrawer.updateCartCount?.(cart.item_count))
        .catch(() => {})
      return data
    }
  }

  class PhsiqWishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.pwd__panel')
      this.list = this.querySelector('[data-pwd-list]')
      this.emptyState = this.querySelector('[data-pwd-empty]')
      this.loading = this.querySelector('[data-pwd-loading]')
      this.footer = this.querySelector('[data-pwd-footer]')
      this.countEl = this.querySelector('[data-pwd-count]')
      this.toast = document.querySelector('[data-pwd-toast]')
      this.isWishlistPage = window.MinimogSettings?.template === 'page.wishlist'
      this.renderToken = 0

      document.addEventListener('click', this.onTriggerClick, true)
      this.addEventListener('click', this.onInnerClick)
      this.addEventListener('change', this.onVariantChange)
      document.addEventListener('keydown', this.onKeydown)
      document.addEventListener('wishlist:updated', this.onWishlistUpdated)
      document.addEventListener('wishlist:added', this.onWishlistAdded)
      this.updateCount(readHandles().length)
    }

    onTriggerClick = (event) => {
      const trigger = event.target.closest(TRIGGERS)
      if (!trigger) return
      // On the wishlist page the header heart keeps its normal link behaviour.
      if (this.isWishlistPage && !trigger.hasAttribute('data-pwd-open')) return
      event.preventDefault()
      event.stopPropagation()
      this.hideToast()
      this.open(trigger)
    }

    onInnerClick = (event) => {
      if (event.target.closest('[data-pwd-close]')) {
        event.preventDefault()
        this.close()
        return
      }
      const removeBtn = event.target.closest('[data-pwd-remove]')
      if (removeBtn) {
        this.removeItem(removeBtn.closest('[data-handle]'))
        return
      }
      const addBtn = event.target.closest('[data-pwd-add]')
      if (addBtn) this.addToCart(addBtn)
    }

    onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close()
    }

    onWishlistUpdated = (event) => {
      const handles = event.detail?.products || readHandles()
      this.updateCount(handles.length)
      if (this.classList.contains('is-open') && !this.skipNextRender) this.render()
      this.skipNextRender = false
    }

    onWishlistAdded = () => {
      if (!this.classList.contains('is-open')) this.showToast()
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement
      clearTimeout(this.hideTimer)
      this.hidden = false
      this.render()
      requestAnimationFrame(() => {
        this.classList.add('is-open')
        document.documentElement.classList.add('pwd-lock')
        this.panel.focus({ preventScroll: true })
      })
    }

    close() {
      this.classList.remove('is-open')
      document.documentElement.classList.remove('pwd-lock')
      clearTimeout(this.hideTimer)
      this.hideTimer = setTimeout(() => {
        if (!this.classList.contains('is-open')) this.hidden = true
      }, 450)
      this.lastFocus?.focus?.({ preventScroll: true })
    }

    updateCount(count) {
      this.countEl.textContent = count
      this.countEl.classList.toggle('is-zero', count === 0)
    }

    async render() {
      const token = ++this.renderToken
      const handles = readHandles()
      this.updateCount(handles.length)

      if (!handles.length) {
        this.list.innerHTML = ''
        this.setState('empty')
        return
      }

      const rendered = [...this.list.children].map((li) => li.dataset.handle)
      if (rendered.join('|') !== handles.join('|')) {
        if (!this.list.children.length) this.setState('loading')
        const products = await Promise.all(handles.map(fetchProduct))
        if (token !== this.renderToken) return
        this.list.innerHTML = products
          .map((product, i) => (product ? this.itemTemplate(product, i) : ''))
          .join('')
      }
      this.setState(this.list.children.length ? 'list' : 'empty')
    }

    setState(state) {
      this.loading.hidden = state !== 'loading'
      this.emptyState.hidden = state !== 'empty'
      this.footer.hidden = state !== 'list'
      this.list.hidden = state !== 'list'
    }

    priceTemplate(variant) {
      const onSale = variant.compare_at_price > variant.price
      return `<span class="pwd-item__price-now${onSale ? ' is-sale' : ''}">${money(variant.price)}</span>
        ${onSale ? `<s class="pwd-item__price-was">${money(variant.compare_at_price)}</s>` : ''}`
    }

    itemTemplate(product, index) {
      const variants = product.variants || []
      const selected = variants.find((v) => v.available) || variants[0]
      if (!selected) return ''
      const image = selected.featured_image?.src || product.featured_image

      const options =
        variants.length > 1
          ? `<label class="pwd-item__select">
              <select data-pwd-variant aria-label="Choose option for ${esc(product.title)}">
                ${variants
                  .map(
                    (v) =>
                      `<option value="${v.id}"${v.id === selected.id ? ' selected' : ''}${v.available ? '' : ' disabled'}>${esc(v.title)}${v.available ? '' : ' — sold out'}</option>`
                  )
                  .join('')}
              </select>
            </label>`
          : ''

      return `
        <li class="pwd-item" data-handle="${esc(product.handle)}" data-variant-id="${selected.id}" style="--i:${index}">
          <a class="pwd-item__media" href="${esc(product.url)}">
            ${image ? `<img src="${esc(sizedImage(image))}" alt="${esc(product.title)}" loading="lazy" width="120" height="150">` : ''}
          </a>
          <div class="pwd-item__info">
            <a class="pwd-item__title" href="${esc(product.url)}">${esc(product.title)}</a>
            <div class="pwd-item__price" data-pwd-price>${this.priceTemplate(selected)}</div>
            ${options}
            <button type="button" class="pwd-item__add" data-pwd-add${selected.available ? '' : ' disabled'}>
              <span>${esc(selected.available ? this.dataset.textAdd : this.dataset.textSoldOut)}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
          <button type="button" class="pwd-item__remove" data-pwd-remove aria-label="Remove ${esc(product.title)} from wishlist">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>
          </button>
        </li>`
    }

    onVariantChange = async (event) => {
      const select = event.target.closest('[data-pwd-variant]')
      if (!select) return
      const item = select.closest('[data-handle]')
      const product = await fetchProduct(item.dataset.handle)
      const variant = product?.variants.find((v) => String(v.id) === select.value)
      if (!variant) return

      item.dataset.variantId = variant.id
      item.querySelector('[data-pwd-price]').innerHTML = this.priceTemplate(variant)
      const img = item.querySelector('.pwd-item__media img')
      if (img && variant.featured_image?.src) img.src = sizedImage(variant.featured_image.src)
      const addBtn = item.querySelector('[data-pwd-add]')
      addBtn.disabled = !variant.available
      addBtn.querySelector('span').textContent = variant.available ? this.dataset.textAdd : this.dataset.textSoldOut
    }

    removeItem(item) {
      if (!item) return
      const handle = item.dataset.handle
      item.classList.add('is-removing')
      setTimeout(() => {
        item.remove()
        if (!this.list.children.length) this.setState('empty')
      }, 380)
      this.skipNextRender = true
      if (window.MinimogTheme?.Wishlist?.removeHandle) {
        window.MinimogTheme.Wishlist.removeHandle(handle)
      } else {
        const handles = readHandles().filter((h) => h !== handle)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(handles))
        this.updateCount(handles.length)
      }
    }

    async addToCart(button) {
      const item = button.closest('[data-handle]')
      const variantId = item?.dataset.variantId
      if (!variantId || button.disabled) return

      const label = button.querySelector('span')
      button.disabled = true
      button.classList.add('is-loading')
      label.textContent = this.dataset.textAdding
      try {
        await window.PhsiqCart.add(variantId, { beforeOpen: () => this.close() })
        button.classList.add('is-added')
        label.textContent = 'Added'
      } catch (err) {
        label.textContent = err.message
        button.classList.add('is-error')
      } finally {
        setTimeout(() => {
          button.disabled = false
          button.classList.remove('is-loading', 'is-added', 'is-error')
          label.textContent = this.dataset.textAdd
        }, 2200)
      }
    }

    showToast() {
      if (!this.toast) return
      clearTimeout(this.toastTimer)
      this.toast.hidden = false
      requestAnimationFrame(() => this.toast.classList.add('is-visible'))
      this.toastTimer = setTimeout(() => this.hideToast(), 3200)
    }

    hideToast() {
      if (!this.toast || this.toast.hidden) return
      this.toast.classList.remove('is-visible')
      setTimeout(() => {
        if (!this.toast.classList.contains('is-visible')) this.toast.hidden = true
      }, 350)
    }
  }

  customElements.define('phsiq-wishlist-drawer', PhsiqWishlistDrawer)
})()
